"use client"

/**
 * Question composer — the field-level UI the builder and inspector are made of.
 *
 * Split out of `new-library-item-form.tsx` alongside `composer-form.ts` so both
 * hosts of the composer body render the same controls. Nothing here owns form
 * state: every piece takes a value and a change handler.
 */

import { useQuestionCategories } from "@/hooks/use-question-categories"
import { KeyMetrics } from "@/components/key-metrics"
import * as React from "react"

import { cn } from "@/lib/utils"
import { useAlignMenuToSelection } from "@/lib/align-menu-to-selection"
import { PBI_EXPLAINER, pbiBand } from "@/lib/pbi-bands"
import { ICON_DISC_TONE_CLASS, type TintedIconDiscTone } from "@/components/tinted-icon-disc-shared"

import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group"
import { Separator } from "@/components/ui/separator"
import { Label } from "@/components/ui/label"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { Kbd } from "@/components/ui/kbd"
import { Textarea } from "@/components/ui/textarea"
import { Tip } from "@/components/ui/tip"
import { FilterChipGroup } from "@/components/ui/filter-chip-group"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import { ToggleSwitch } from "@/components/ui/toggle-switch"
import { LeoAskPrompt } from "@/components/assessment-structure/leo-ask-prompt"
import { leoComposerOptionAsk } from "@/lib/leo-composer-ask"
import { ComposerFieldSelectionAsk } from "./composer-selection-ask"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useClassificationLookups } from "@/hooks/use-classification-lookups"
import { useCourseAssignments } from "@/hooks/use-course-assignments"
import { appliesToCourse, levelMappingKey } from "@/lib/exam-categories/course-assignment"
import { activeOptions, joinTags, kindOf, tagValues, type LookupLevel } from "@/lib/classification/lookups"
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { builderListIndexClass } from "@/components/question-builder-primitives-shared"
import { BUILDER_LIST_FIELD, BUILDER_LIST_LOCKED, BUILDER_LIST_ROW, BuilderAnswerChip, BuilderListRow, BuilderListRowActions, BuilderSection } from "@/components/question-builder-primitives"
import { ExamCategoryPickerPanel } from "@/components/exam-category-picker-panel"
import { LibraryFolderPickerPanel } from "@/components/library-folder-picker-panel"
import { useExamCategories } from "@/hooks/use-exam-categories"
import { categoriesInDimension, findCategory } from "@/lib/exam-categories"
import {
  AUTHORING_REFERENCE_KIND_OPTIONS,
  authoringQuestionType,
  type AuthoringQuestionType,
  type AuthoringQuestionTypeOption,
  type AuthoringReference,
  type AuthoringReferenceKind,
} from "@/lib/library-authoring"
import type { LibraryBloomLevel } from "@/lib/mock/library"
import type {
  LibraryFolder,
  LibraryFolderColorKey,
} from "@/lib/mock/library-folders"

import {
  categoryBreadcrumb,
  difficultyInsightForFolder,
  folderBreadcrumb,
} from "./composer-form"
import { LeoApplyField } from "@/components/question-quality/leo-apply-motion"
import { FillBlankRichTextEditor } from "./fill-blank-rich-text-editor"
import { COMPOSER_INPUT_SHELL_BORDER_CLASS, INSPECTOR_PICKER_TRIGGER_CLASS } from "@/components/question-composer/composer-fields-shared"

export { COMPOSER_RICH_TEXT_EDITOR_SHELL_CLASS } from "./composer-input-chrome"

const COMPOSER_INPUT_SHELL_FOCUS_WITHIN_CLASS =
  "focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50"

/** Shared inspector grouping — matches the Classification fields ({@link DifficultyField}). */
export const INSPECTOR_SECTION_CARD_CLASS =
  "flex flex-col gap-3 rounded-lg border border-border bg-muted/20 p-3"

export function InspectorSectionCard({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn(INSPECTOR_SECTION_CARD_CLASS, className)}>{children}</div>
  )
}

export function InspectorToggleRow({
  id,
  label,
  description,
  checked,
  onCheckedChange,
  disabled,
  variant = "card",
}: {
  id: string
  label: string
  /** Only for toggles whose label does not say what happens to the score. */
  description?: string
  checked: boolean
  onCheckedChange: (next: boolean) => void
  disabled?: boolean
  /** `card` = standalone bordered row; `inset` = row inside {@link InspectorSectionCard}. */
  variant?: "card" | "inset"
}) {
  if (variant === "inset") {
    return (
      <div
        className={cn(
          "flex items-start justify-between gap-3",
          disabled && "opacity-60",
        )}
      >
        <div className="min-w-0 flex-1 space-y-0.5">
          <Label
            htmlFor={id}
            className="text-sm leading-snug font-medium text-foreground"
          >
            {label}
          </Label>
          {description ? (
            <p className="text-xs leading-snug text-muted-foreground">
              {description}
            </p>
          ) : null}
        </div>
        <ToggleSwitch
          id={id}
          checked={checked}
          onChange={onCheckedChange}
          disabled={disabled}
        />
      </div>
    )
  }

  return (
    <div
      className={cn(
        "flex items-start justify-between gap-3 rounded-lg border border-border bg-muted/10 px-3 py-2.5",
        disabled && "opacity-60",
      )}
    >
      <div className="min-w-0 flex-1 space-y-0.5">
        <Label
          htmlFor={id}
          className="text-sm leading-snug font-medium text-foreground"
        >
          {label}
        </Label>
        {description ? (
          <p className="text-xs leading-snug text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>
      <ToggleSwitch
        id={id}
        checked={checked}
        onChange={onCheckedChange}
        disabled={disabled}
      />
    </div>
  )
}

export function InspectorSection({
  title,
  htmlFor,
  children,
  description,
  actions,
}: {
  title: string
  htmlFor?: string
  children: React.ReactNode
  description?: React.ReactNode
  actions?: React.ReactNode
}) {
  if (htmlFor) {
    return (
      <section className="flex flex-col gap-2">
        <Label
          htmlFor={htmlFor}
          className="text-sm font-semibold text-foreground"
        >
          {title}
        </Label>
        {children}
        {description ? (
          <p className="text-xs leading-snug text-muted-foreground">
            {description}
          </p>
        ) : null}
      </section>
    )
  }

  return (
    <BuilderSection title={title} actions={actions} description={description}>
      {children}
    </BuilderSection>
  )
}

function parseMarksValue(value: string, fallback = 1): number {
  const trimmed = value.trim()
  if (!trimmed) return fallback
  const n = Number.parseFloat(trimmed)
  return Number.isFinite(n) ? n : fallback
}

/** Segmented `[- | value | +]` marks control — `InputGroup` shell + dividers. */
export function MarksInputStepper({
  id,
  value,
  placeholder = "1",
  disabled,
  onChange,
  className,
}: {
  id: string
  value: string
  placeholder?: string
  disabled?: boolean
  onChange: (next: string) => void
  className?: string
}) {
  const bumpMarks = (delta: number) => {
    const current = parseMarksValue(value, parseMarksValue(placeholder))
    const next = Math.max(0, current + delta)
    onChange(next <= 0 ? "" : String(next))
  }

  const stepperDividerClass =
    "h-auto shrink-0 self-stretch bg-border data-[orientation=vertical]:h-auto data-[orientation=vertical]:w-px"

  return (
    <InputGroup
      aria-label="Marks"
      className={cn(
        "h-9 w-auto shrink-0 overflow-hidden",
        disabled && "pointer-events-none opacity-50",
        className,
      )}
    >
      <InputGroupAddon align="inline-start" className="!ms-0 p-0 ps-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <InputGroupButton
              type="button"
              size="icon-sm"
              aria-label="Decrease marks"
              disabled={disabled}
              onClick={() => bumpMarks(-1)}
              className="size-9 rounded-none bg-transparent text-base shadow-none hover:bg-muted/60"
            >
              <i className="fa-light fa-minus text-base" aria-hidden="true" />
            </InputGroupButton>
          </TooltipTrigger>
          <TooltipContent side="top">Decrease marks</TooltipContent>
        </Tooltip>
      </InputGroupAddon>
      <Separator
        orientation="vertical"
        decorative
        className={stepperDividerClass}
      />
      <InputGroupInput
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        inputMode="decimal"
        placeholder={placeholder}
        disabled={disabled}
        aria-label="Marks"
        className="w-10 min-w-0 max-w-10 border-0 px-0.5 text-center text-lg font-semibold tabular-nums shadow-none ring-0 focus-visible:border-transparent focus-visible:ring-0"
      />
      <Separator
        orientation="vertical"
        decorative
        className={stepperDividerClass}
      />
      <InputGroupAddon align="inline-end" className="!me-0 p-0 pe-0">
        <Tooltip>
          <TooltipTrigger asChild>
            <InputGroupButton
              type="button"
              size="icon-sm"
              aria-label="Increase marks"
              disabled={disabled}
              onClick={() => bumpMarks(1)}
              className="size-9 rounded-none bg-transparent text-base shadow-none hover:bg-muted/60"
            >
              <i className="fa-light fa-plus text-base" aria-hidden="true" />
            </InputGroupButton>
          </TooltipTrigger>
          <TooltipContent side="top">Increase marks</TooltipContent>
        </Tooltip>
      </InputGroupAddon>
    </InputGroup>
  )
}

const COMPOSER_TAGS_FIELD_SHELL = cn(
  COMPOSER_INPUT_SHELL_BORDER_CLASS,
  COMPOSER_INPUT_SHELL_FOCUS_WITHIN_CLASS,
  "flex min-w-0 flex-wrap items-center gap-1.5 overflow-hidden px-2.5 py-1.5 has-disabled:opacity-50",
)

/** Tags composer — tags + draft input in one wrapping row. */
export function ComposerTagsField({
  id,
  tags,
  draft,
  disabled,
  onDraftChange,
  onCommit,
  onRemove,
}: {
  id: string
  tags: string[]
  draft: string
  disabled?: boolean
  onDraftChange: (next: string) => void
  onCommit: () => void
  onRemove: (tag: string) => void
}) {
  return (
    <div
      className={COMPOSER_TAGS_FIELD_SHELL}
      role={tags.length > 0 ? "list" : undefined}
      aria-label={tags.length > 0 ? "Added tags" : undefined}
    >
      {tags.length > 0
        ? tags.map((tag) => (
            <Badge
              key={tag}
              variant="secondary"
              asChild
              role="listitem"
              className="h-auto max-w-full min-w-0 shrink whitespace-normal px-1.5 py-0.5 text-xs font-medium leading-snug"
            >
              <button
                type="button"
                onClick={() => onRemove(tag)}
                aria-label={`Remove tag ${tag}`}
                disabled={disabled}
                className="flex max-w-full min-w-0 items-start gap-1 text-left"
              >
                <span className="min-w-0 break-all">#{tag}</span>
                <i
                  className="fa-light fa-xmark mt-0.5 shrink-0"
                  data-icon="inline-end"
                  aria-hidden="true"
                />
              </button>
            </Badge>
          ))
        : null}
      <input
        id={id}
        value={draft}
        disabled={disabled}
        onChange={(event) => onDraftChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === ",") {
            event.preventDefault()
            onCommit()
          }
        }}
        onBlur={onCommit}
        placeholder={tags.length > 0 ? "Add another tag…" : "STEMI, antibiotics…"}
        aria-label="Add tags"
        className={cn(
          "min-h-6 border-0 bg-transparent p-0 text-xs text-foreground outline-none",
          "placeholder:text-muted-foreground disabled:cursor-not-allowed md:text-sm",
          tags.length > 0 ? "min-w-[6rem] flex-1 basis-20" : "w-full min-w-0 flex-1",
        )}
      />
    </div>
  )
}

/** Field labels on the composer builder + inspector (Title, Question, Marks, …). */
export const COMPOSER_FIELD_LABEL_CLASS =
  "text-xs font-medium text-muted-foreground"

/** Subsection label inside inspector cards (Difficulty, Discrimination, …). */
export function InspectorSubsectionLabel({
  children,
  htmlFor,
}: {
  children: React.ReactNode
  htmlFor?: string
}) {
  return (
    <Label
      htmlFor={htmlFor}
      className="text-xs font-medium text-muted-foreground"
    >
      {children}
    </Label>
  )
}

/** Builder canvas — format, stem, references, type-specific blocks, rationale.
 *  Uncarded so the page dot field reads through; fields keep their own surfaces. */
/**
 * Fields and outline buttons on the builder sit on a solid surface, so the
 * page's dot canvas never shows through them (text inputs, textareas, outline
 * and secondary-style buttons; filled buttons already have their own fill).
 */
const BUILDER_SOLID_CONTROLS = cn(
  "[&_textarea]:bg-background [&_input:not([type=checkbox]):not([type=radio])]:bg-background [&_[data-variant=outline]]:bg-background",
  "dark:[&_textarea]:bg-input-background dark:[&_input:not([type=checkbox]):not([type=radio])]:bg-input-background dark:[&_[data-variant=outline]]:bg-input-background",
)

export function QuestionBuilderCard({
  children,
  pageScroll = false,
}: {
  children: React.ReactNode
  pageScroll?: boolean
}) {
  if (pageScroll) {
    return (
      <div className="w-full min-w-0">
        <div className={cn("flex flex-col gap-4 px-1 pt-0 pb-3 sm:px-2 sm:pt-0 sm:pb-3", BUILDER_SOLID_CONTROLS)}>
          {children}
        </div>
      </div>
    )
  }

  return (
    <div className="flex max-h-full min-h-0 w-full min-w-0 flex-1 flex-col overflow-hidden">
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-1 py-1 pb-3 [-webkit-overflow-scrolling:touch] sm:px-2 sm:py-1 sm:pb-3">
        <div className={cn("flex flex-col gap-4", BUILDER_SOLID_CONTROLS)}>{children}</div>
      </div>
    </div>
  )
}

/** Past this the stem scrolls internally instead of pushing the card body away. */
const LEAD_IN_MAX_HEIGHT_PX = 384

/**
 * Textarea that grows with its content. The stem field used to trap long
 * questions in a 3-row box: the native resize handle stalls as soon as it
 * reaches the bottom of the card's scroll viewport, because the card does not
 * auto-scroll mid-drag — so authors could not open the field up. Autosizing
 * follows `AskLeoComposer` (measure `scrollHeight`, no `field-sizing`), caps at
 * `maxHeight`, and steps aside once the author sets a height by hand.
 */
export function AutosizeTextarea({
  maxHeight = LEAD_IN_MAX_HEIGHT_PX,
  ref,
  ...props
}: React.ComponentProps<typeof Textarea> & { maxHeight?: number }) {
  const elRef = React.useRef<HTMLTextAreaElement | null>(null)
  const appliedInlineHeightRef = React.useRef<string | null>(null)
  const widthRef = React.useRef<number | null>(null)
  const authorResizedRef = React.useRef(false)
  const observedRef = React.useRef<HTMLTextAreaElement | null>(null)
  const observerRef = React.useRef<ResizeObserver | null>(null)

  const fit = React.useCallback(() => {
    const el = elRef.current
    if (!el || authorResizedRef.current) return
    el.style.height = "auto"
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`
    el.style.overflowY = el.scrollHeight > maxHeight ? "auto" : "hidden"
    appliedInlineHeightRef.current = el.style.height
    widthRef.current = el.offsetWidth
  }, [maxHeight])

  const bindTextareaRef = React.useCallback(
    (el: HTMLTextAreaElement | null) => {
      observerRef.current?.disconnect()
      observerRef.current = null
      elRef.current = el

      if (el) {
        authorResizedRef.current = false
        observedRef.current = el
        fit()

        if (typeof ResizeObserver !== "undefined") {
          const observer = new ResizeObserver(() => {
            if (el.offsetWidth !== widthRef.current) {
              fit()
              return
            }
            if (el.style.height !== appliedInlineHeightRef.current) {
              authorResizedRef.current = true
            }
          })
          observer.observe(el)
          observerRef.current = observer
        }
      } else {
        observedRef.current = null
      }

      if (typeof ref === "function") ref(el)
      else if (ref) ref.current = el
    },
    [fit, ref],
  )

  React.useLayoutEffect(() => {
    fit()
  })

  React.useEffect(
    () => () => {
      observerRef.current?.disconnect()
      observerRef.current = null
    },
    [],
  )

  return (
    <Textarea
      {...props}
      ref={bindTextareaRef}
    />
  )
}

/** Fill-in-the-blank question stem — rich text with inline blank chips. */
export function FillBlankLeadInField({
  id = "qb-lead-in",
  value,
  onChange,
  disabled,
}: {
  id?: string
  value: string
  onChange: (value: string) => void
  disabled?: boolean
}) {
  return (
    <FillBlankRichTextEditor
      id={id}
      value={value}
      onChange={onChange}
      disabled={disabled}
      aria-required="true"
      aria-label="Question"
    />
  )
}

// ─── Folder picker (Popover + Library secondary tree) ───────────────────────
//
// Compact selected tile — same visual rhythm as the collapsed
// "Question format" card. Popover uses `LibraryFolderPickerPanel` (search +
// folder tree aligned with the Library secondary panel).

const FOLDER_TINT_BG: Record<LibraryFolderColorKey, string> = {
  brand:
    "bg-[var(--icon-disc-brand-bg)] text-[var(--icon-disc-brand-fg)]",
  success:
    "bg-[var(--icon-disc-chart-2-bg)] text-[var(--icon-disc-chart-2-fg)]",
  warning:
    "bg-[var(--icon-disc-chart-4-bg)] text-[var(--icon-disc-chart-4-fg)]",
  destructive:
    "bg-[var(--icon-disc-danger-bg)] text-[var(--icon-disc-danger-fg)]",
  muted: "bg-muted text-muted-foreground",
  // Washed out, with the icon pulled toward the text colour: a chart colour
  // on a tint of itself falls short of 3:1 (WCAG 1.4.11) for the glyph.
  chart1:
    "bg-[color-mix(in_oklch,var(--color-chart-1)_10%,var(--background))] text-[color-mix(in_oklch,var(--color-chart-1)_45%,var(--foreground))]",
  chart2: "bg-[var(--icon-disc-chart-2-bg)] text-[var(--icon-disc-chart-2-fg)]",
  chart3:
    "bg-[color-mix(in_oklch,var(--color-chart-3)_10%,var(--background))] text-[color-mix(in_oklch,var(--color-chart-3)_45%,var(--foreground))]",
}

/** WCAG 1.4.11 icon-disc tokens only — four a11y pairings; reuse across formats. */
const QUESTION_FORMAT_ICON_TONE: Record<AuthoringQuestionType, TintedIconDiscTone> =
  {
    mcq_single: "brand",
    mcq_multiple: "chart-2",
    true_false: "chart-4",
    short_answer: "destructive",
    numeric: "brand",
    essay: "destructive",
    fill_blank: "chart-2",
    matching: "chart-4",
    ordering: "brand",
    hotspot: "chart-2",
  }

function questionFormatIconTileClass(
  type: AuthoringQuestionType,
  size: "trigger" | "list" = "trigger",
) {
  const tone = QUESTION_FORMAT_ICON_TONE[type] ?? "brand"
  return cn(
    "inline-flex shrink-0 items-center justify-center rounded-md hc:border hc:border-foreground forced-colors:border forced-colors:border-[CanvasText] forced-colors:bg-[Canvas] forced-colors:text-[CanvasText]",
    size === "trigger" ? "size-9" : "mt-0.5 size-8",
    ICON_DISC_TONE_CLASS[tone],
  )
}

interface FolderPickerControlProps {
  folders: LibraryFolder[]
  value: string[]
  onChange: (ids: string[]) => void
  open: boolean
  onOpenChange: (open: boolean) => void
  onRequestNewFolder: () => void
  /** Sheet and other stacked surfaces pass a higher z-index. */
  contentClassName?: string
  /** When false, the panel does not offer New folder. */
  showNewFolder?: boolean
  /** Inspector stays multi. Create with Leo course pick is one folder. */
  multiple?: boolean
  emptyLabel?: string
  emptyHint?: string
  /** Noun in the trigger name, e.g. "course". Defaults to locations. */
  ariaNoun?: string
  /** Tray pickers sit low in Ask Leo, so the list opens up. */
  side?: "top" | "bottom"
  avoidCollisions?: boolean
}

export function FolderPickerControl({
  folders,
  value,
  onChange,
  open,
  onOpenChange,
  onRequestNewFolder,
  contentClassName,
  showNewFolder = true,
  multiple = true,
  emptyLabel,
  emptyHint,
  ariaNoun,
  side = "bottom",
  avoidCollisions = false,
}: FolderPickerControlProps) {
  const contentRef = React.useRef<HTMLDivElement>(null)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  useAlignMenuToSelection(open, contentRef, triggerRef)
  const valueIds = new Set(value)
  const selected = folders.filter((f) => valueIds.has(f.id))
  const shown = multiple ? selected : selected.slice(0, 1)
  const primary = shown[0]
  const tint = primary
    ? FOLDER_TINT_BG[primary.colorKey]
    : FOLDER_TINT_BG.muted
  const locationLabel =
    shown.length === 0
      ? (emptyLabel ?? "Pick locations")
      : shown.length === 1
        ? primary!.name
        : `${shown.length} ${ariaNoun === "course" ? "courses" : "folders"}`
  const locationHint =
    shown.length === 0
      ? (emptyHint ?? "Required — choose one or more")
      : shown.length === 1
        ? primary!.parentId
          ? `in ${folders.find((p) => p.id === primary!.parentId)?.name}`
          : ariaNoun === "course"
            ? "Course"
            : "Top-level folder"
        : shown.map((f) => f.name).join(", ")
  const triggerName =
    shown.length > 0
      ? ariaNoun
        ? `Change ${ariaNoun}, currently ${shown
            .map((f) => folderBreadcrumb(f.id, folders))
            .join(", ")}`
        : `Change locations — currently ${shown
            .map((f) => folderBreadcrumb(f.id, folders))
            .join(", ")}`
      : (emptyLabel ?? (ariaNoun ? `Pick a ${ariaNoun}` : "Pick locations"))

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <button
          ref={triggerRef}
          type="button"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={triggerName}
          className={INSPECTOR_PICKER_TRIGGER_CLASS}
        >
          <span
            className={cn(
              "inline-flex size-9 shrink-0 items-center justify-center rounded-md",
              tint,
            )}
            aria-hidden="true"
          >
            <i
              className={cn(
                "fa-light text-base",
                primary?.icon ?? "fa-folder"
              )}
            />
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm font-medium text-foreground">
              {locationLabel}
            </span>
            <span className="truncate text-xs text-muted-foreground">
              {locationHint}
            </span>
          </span>
          <span
            className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors group-hover:bg-muted group-hover:text-foreground"
            aria-hidden="true"
          >
            <i className="fa-light fa-chevron-down text-xs" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        align="start"
        side={side}
        sideOffset={0}
        avoidCollisions={avoidCollisions}
        sticky="always"
        className={cn(
          "z-[80] w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0",
          "flex max-h-[min(20rem,50vh)] flex-col overflow-hidden",
          contentClassName,
        )}
      >
        <LibraryFolderPickerPanel
          key={open ? "open" : "closed"}
          folders={folders}
          value={multiple ? value : value.slice(0, 1)}
          multiple={multiple}
          noun={ariaNoun === "course" ? "course" : "folder"}
          onChange={(ids) => {
            onChange(multiple ? ids : ids.slice(0, 1))
            if (!multiple) onOpenChange(false)
          }}
          onRequestNewFolder={onRequestNewFolder}
          showNewFolderFooter={showNewFolder}
        />
      </PopoverContent>
    </Popover>
  )
}

interface CategoryPickerControlProps {
  value: string[]
  onChange: (ids: string[]) => void
  open: boolean
  onOpenChange: (open: boolean) => void
  disabled?: boolean
  contentClassName?: string
  /** Inspector stays multi. Create with Leo category pick is one node. */
  multiple?: boolean
  emptyLabel?: string
  emptyHint?: string
  ariaNoun?: string
  side?: "top" | "bottom"
  avoidCollisions?: boolean
  /** Opens navigation in the parent panel instead of a floating menu. */
  navigateInPanel?: boolean
}

export function CategoryPickerControl({
  value,
  onChange,
  open,
  onOpenChange,
  disabled = false,
  contentClassName,
  multiple = true,
  emptyLabel,
  emptyHint,
  ariaNoun,
  side = "bottom",
  avoidCollisions = false,
  navigateInPanel = false,
}: CategoryPickerControlProps) {
  const contentRef = React.useRef<HTMLDivElement>(null)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  useAlignMenuToSelection(open, contentRef, triggerRef)
  const { categories, tree } = useQuestionCategories()
  const selectedIds = value ?? []
  // Hoisted once: tested for every category in the list below.
  const selectedIdSet = new Set(selectedIds)
  const selected = categories.filter((category) => selectedIdSet.has(category.id))
  const shown = multiple ? selected : selected.slice(0, 1)
  const primary = shown[0]
  const parent = primary?.parentId
    ? findCategory(categories, primary.parentId)
    : undefined
  const categoryLabel =
    shown.length === 0
      ? (emptyLabel ?? "Pick categories")
      : shown.length === 1
        ? primary!.name
        : `${shown.length} categories`
  const categoryHint =
    shown.length === 0
      ? (emptyHint ?? "Choose one or more")
      : shown.length === 1
        ? parent
          ? `in ${parent.name}`
          : "Domain"
        : shown.map((category) => category.name).join(", ")
  const triggerName =
    shown.length > 0
      ? ariaNoun
        ? `Change ${ariaNoun}, currently ${shown
            .map((category) => categoryBreadcrumb(category.id, categories))
            .join(", ")}`
        : `Change categories, currently ${shown
            .map((category) => categoryBreadcrumb(category.id, categories))
            .join(", ")}`
      : (emptyLabel ?? (ariaNoun ? `Pick a ${ariaNoun}` : "Pick categories"))

  const trigger = (
        <button
          ref={navigateInPanel ? undefined : triggerRef}
          type="button"
          disabled={disabled}
          aria-haspopup={navigateInPanel ? undefined : "listbox"}
          aria-expanded={navigateInPanel ? undefined : open}
          aria-label={triggerName}
          className={cn(
            INSPECTOR_PICKER_TRIGGER_CLASS,
            disabled && "cursor-not-allowed opacity-60",
          )}
          onClick={
            navigateInPanel
              ? () => {
                  if (!disabled) onOpenChange(true)
                }
              : undefined
          }
        >
          <span
            className={cn(
              "inline-flex size-9 shrink-0 items-center justify-center rounded-md",
              primary ? FOLDER_TINT_BG.brand : FOLDER_TINT_BG.muted,
            )}
            aria-hidden="true"
          >
            <i className="fa-light fa-tag text-base" />
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm font-medium text-foreground">
              {categoryLabel}
            </span>
            <span className="truncate text-xs text-muted-foreground">
              {categoryHint}
            </span>
          </span>
          <span
            className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors group-hover:bg-muted group-hover:text-foreground"
            aria-hidden="true"
          >
            <i className={cn("fa-light text-xs", navigateInPanel ? "fa-chevron-right" : "fa-chevron-down")} />
          </span>
        </button>
  )

  if (navigateInPanel) return trigger

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (disabled) return
        onOpenChange(next)
      }}
    >
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        ref={contentRef}
        align="start"
        side={side}
        sideOffset={0}
        avoidCollisions={avoidCollisions}
        sticky="always"
        className={cn(
          "z-[80] w-[var(--radix-popover-trigger-width)] min-w-[280px] p-0",
          "flex max-h-[min(20rem,50vh)] flex-col overflow-hidden",
          contentClassName,
        )}
      >
        <ExamCategoryPickerPanel
          key={open ? "open" : "closed"}
          categories={categories}
          tree={tree}
          value={multiple ? selectedIds : selectedIds.slice(0, 1)}
          multiple={multiple}
          onChange={(ids) => {
            onChange(multiple ? ids : ids.slice(0, 1))
            if (!multiple) onOpenChange(false)
          }}
        />
      </PopoverContent>
    </Popover>
  )
}

interface FormatPickerControlProps {
  options: readonly AuthoringQuestionTypeOption[]
  value: AuthoringQuestionType
  onChange: (next: AuthoringQuestionType) => void
  disabled?: boolean
  id?: string
}

export function FormatPickerControl({
  options,
  value,
  onChange,
  disabled,
  id = "qb-format",
}: FormatPickerControlProps) {
  const [open, setOpen] = React.useState(false)
  const selected = authoringQuestionType(value)

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (disabled) return
        setOpen(next)
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          id={id}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={`Question format: ${selected.label}`}
          className={cn(
            INSPECTOR_PICKER_TRIGGER_CLASS,
            disabled && "cursor-not-allowed opacity-60",
          )}
        >
          <span
            className={questionFormatIconTileClass(value, "trigger")}
            aria-hidden="true"
          >
            <i className={cn("fa-light text-base", selected.icon)} />
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-sm font-medium text-foreground">
              {selected.shortLabel}
            </span>
            <span className="truncate text-xs text-muted-foreground">
              {selected.tileSummary}
            </span>
          </span>
          <span
            className="inline-flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors group-hover:bg-muted group-hover:text-foreground"
            aria-hidden="true"
          >
            <i className="fa-light fa-chevron-down text-xs" />
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={6}
        collisionPadding={16}
        className="w-[var(--radix-popover-trigger-width)] min-w-[280px] p-1"
      >
        <div
          role="listbox"
          aria-label="Question format"
          className="max-h-[min(22rem,var(--radix-popover-content-available-height))] overflow-y-auto"
        >
          {options.map((option) => {
            const isSelected = option.value === value
            return (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={cn(
                  "flex w-full items-start gap-3 rounded-md px-2.5 py-2 text-left transition-colors",
                  isSelected ? "bg-muted/70 text-foreground" : "hover:bg-muted/60",
                )}
                onClick={() => {
                  onChange(option.value)
                  setOpen(false)
                }}
              >
                <span
                  className={questionFormatIconTileClass(option.value, "list")}
                  aria-hidden="true"
                >
                  <i className={cn("fa-light text-sm", option.icon)} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-medium">{option.shortLabel}</span>
                    {isSelected ? (
                      <i
                        className="fa-solid fa-check text-xs text-foreground"
                        aria-hidden="true"
                      />
                    ) : null}
                  </span>
                  <span className="mt-0.5 block text-xs text-muted-foreground">
                    {option.tileSummary}
                  </span>
                </span>
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}

// ─── Difficulty and discrimination ───────────────────────────────────────────
//
// Two different facts, which this card used to fuse under one "Predicted
// difficulty" heading with a bare PBI number underneath:
//
//   Difficulty     — how hard examinees find the item. Leo predicts it from the
//                    content (stem length, option count, Bloom's level,
//                    vocabulary); the author can override to Manual.
//   Discrimination — how well the item separates strong from weak examinees,
//                    reported as a PBI score in a square tile with the band
//                    meaning under it. It comes from exam history, so the
//                    author cannot set it, and a new item simply has none.
//
// Bands come from `lib/pbi-bands` so this agrees with the Mix tab.

function DifficultyDiscriminationReading({
  insight,
}: {
  insight: ReturnType<typeof difficultyInsightForFolder>
}) {
  const band = pbiBand(insight.pbi)
  const score = insight.pbi.toFixed(2)
  return (
    <div className="flex flex-col gap-2">
      {/*
        The trigger is the info icon, not the whole row. The row was a
        bare `tabIndex` span wrapping the label — a tab stop announced as
        nothing, on something there is no action to take. Moving the tip
        onto its own button keeps the keyboard path to the explainer and
        leaves the label a label.
      */}
      <span className="inline-flex min-h-6 items-center gap-1.5">
        <InspectorSubsectionLabel>Discrimination</InspectorSubsectionLabel>
        <Tip side="top" label={PBI_EXPLAINER}>
          <button
            type="button"
            aria-label="What discrimination measures"
            className="rounded-1 focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none"
          >
            <i
              className="fa-light fa-circle-info text-xs text-muted-foreground"
              aria-hidden="true"
            />
          </button>
        </Tip>
      </span>
      {/* The package KPI tile, not a one-off: same type, spacing and tone as
          every other metric, and a weak band raises its alert state. */}
      <div
        role="group"
        aria-label={`PBI ${score}. ${band.label}. ${PBI_EXPLAINER}`}
      >
        <KeyMetrics
          variant="cards"
          size="sm"
          showHeader={false}
          metricsSingleRow
          // The strip carries page-level padding for a hub; in the inspector it
          // has to sit on the same left edge as the label above it.
          className="px-0 py-0 lg:px-0 sm:py-0"
          metrics={[
            {
              id: "pbi",
              label: "PBI",
              value: score,
              delta: "",
              trend: "neutral",
              description: band.label,
              ...(band.key === "weak" ? { alert: "danger" as const } : {}),
            },
          ]}
        />
      </div>
    </div>
  )
}

/** Active levels, plus the one already on the question if it has since been switched off. */
function withCurrent(
  options: { value: string; label: string }[],
  levels: readonly LookupLevel[],
  current: string | undefined,
) {
  if (!current || options.some((option) => option.value === current)) return options
  const level = levels.find((entry) => entry.value === current)
  return level ? [...options, { value: level.value, label: level.label }] : options
}

/** Levels of one list for this question: offered, mapped to its course, plus the one it holds. */
function useLevelOptions(kind: string, levels: readonly LookupLevel[], current: string, courseId?: string) {
  const { assignments } = useCourseAssignments()
  return withCurrent(
    activeOptions([...levels]).filter((option) => appliesToCourse(assignments, levelMappingKey(kind, option.value), courseId)),
    levels,
    current,
  )
}

/*
 * One field per classification type, named and ordered as Settings ›
 * Classification has them; the inspector lays them out in that order. Each
 * reads the central lookups: renamed, switched-off or course-limited levels
 * change here too, and a level the question already holds stays shown.
 */

export function DifficultyField({
  label,
  value,
  onChange,
  courseId,
}: {
  label: string
  value: "easy" | "medium" | "hard" | ""
  onChange: (next: "easy" | "medium" | "hard") => void
  courseId?: string
}) {
  const { lookups } = useClassificationLookups()
  const options = useLevelOptions("difficulty", lookups.difficulty, value, courseId)
  return (
    <LeoApplyField anchor="difficulty" revealBlobs>
      <div className="flex flex-col gap-2">
        <InspectorSubsectionLabel>{label}</InspectorSubsectionLabel>
        <FilterChipGroup
          value={value}
          onValueChange={(v) => onChange(v as "easy" | "medium" | "hard")}
          options={options}
          variant="brand"
          size="sm"
          aria-label={label}
        />
      </div>
    </LeoApplyField>
  )
}

export function BloomField({
  label,
  value,
  onChange,
  courseId,
}: {
  label: string
  value: string | undefined
  onChange: (next: LibraryBloomLevel) => void
  courseId?: string
}) {
  const { lookups } = useClassificationLookups()
  const options = useLevelOptions("bloom", lookups.bloom, value ?? "", courseId)
  return (
    <LeoApplyField anchor="bloom" revealBlobs>
      <div className="flex flex-col gap-2">
        <InspectorSubsectionLabel>{label}</InspectorSubsectionLabel>
        <FilterChipGroup
          value={value ?? ""}
          onValueChange={(v) => onChange(v as LibraryBloomLevel)}
          options={options}
          variant="brand"
          size="sm"
          aria-label={label}
        />
      </div>
    </LeoApplyField>
  )
}

/**
 * A hierarchy the admin added: the categories this question carries from its
 * tree, and a way into the tree. Its tags are category ids, kept with the
 * question's other categories.
 */
export function CustomTreeField({
  label,
  dimensionId,
  value,
  onOpen,
  disabled = false,
}: {
  label: string
  dimensionId: string
  value: readonly string[]
  onOpen: () => void
  disabled?: boolean
}) {
  const { categories } = useExamCategories()
  const inTree = React.useMemo(() => {
    const ids = new Set(categoriesInDimension(categories, dimensionId).map((category) => category.id))
    return categories.filter((category) => ids.has(category.id) && value.includes(category.id))
  }, [categories, dimensionId, value])
  const MAX_CHIPS = 3
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <InspectorSubsectionLabel>{label}</InspectorSubsectionLabel>
        <Button type="button" variant="ghost" size="sm" className="ms-auto h-6 px-2 text-xs" disabled={disabled} onClick={onOpen}>
          {inTree.length > 0 ? "Change" : "Choose"}
        </Button>
      </div>
      {inTree.length > 0 ? (
        <ul aria-label={`${label} on this question`} className="m-0 flex list-none flex-wrap gap-1.5 p-0">
          {inTree.slice(0, MAX_CHIPS).map((category) => (
            <li key={category.id}>
              <Badge variant="outline" className="max-w-full font-normal">
                <span className="truncate">{category.name}</span>
              </Badge>
            </li>
          ))}
          {inTree.length > MAX_CHIPS ? (
            <li>
              <Badge variant="secondary" className="font-normal tabular-nums">
                +{inTree.length - MAX_CHIPS} more
              </Badge>
            </li>
          ) : null}
        </ul>
      ) : (
        <p className="m-0 text-xs text-muted-foreground">Nothing chosen yet.</p>
      )}
    </div>
  )
}

/**
 * Chips that each toggle: a tags type, where a question takes as many as apply.
 * Styled as the package's brand FilterChipGroup, which only picks one.
 */
function MultiChipGroup({
  values,
  options,
  onToggle,
  "aria-label": ariaLabel,
}: {
  values: readonly string[]
  options: readonly { value: string; label: string }[]
  onToggle: (value: string) => void
  "aria-label": string
}) {
  return (
    <div role="group" aria-label={ariaLabel} className="flex flex-wrap gap-2">
      {options.map((option) => {
        const on = values.includes(option.value)
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={on}
            onClick={() => onToggle(option.value)}
            className={cn(
              "inline-flex h-7 shrink-0 cursor-pointer select-none items-center gap-1 rounded-full border px-2.5 text-xs font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              on
                ? "border-brand bg-brand text-brand-foreground hover:bg-brand/90"
                : "border-border bg-card text-foreground hover:border-brand/40 hover:bg-interactive-hover-soft",
            )}
          >
            {on ? <i className="fa-solid fa-check text-[0.625rem]" aria-hidden="true" /> : null}
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

/** One of the admin's own types: a pick list of its levels, or tags to pick several of. */
export function CustomClassificationField({
  typeId,
  value,
  onChange,
  courseId,
}: {
  typeId: string
  value: Record<string, string> | undefined
  onChange: (next: Record<string, string>) => void
  courseId?: string
}) {
  const { lookups } = useClassificationLookups()
  const type = lookups.custom.find((entry) => entry.id === typeId)
  const current = value?.[typeId] ?? ""
  const tags = type && kindOf(type) === "tags"
  const picked = tags ? tagValues(current) : []
  const offered = useLevelOptions(typeId, type?.levels ?? [], tags ? "" : current, courseId)
  // A tag the question holds stays visible after it is hidden or unmapped.
  const options = tags
    ? [
        ...offered,
        ...picked
          .filter((v) => !offered.some((o) => o.value === v))
          .map((v) => ({ value: v, label: type?.levels.find((l) => l.value === v)?.label ?? v })),
      ]
    : offered
  if (!type || options.length === 0) return null
  const save = (next: string) => {
    const updated = { ...(value ?? {}) }
    if (next) updated[typeId] = next
    else delete updated[typeId]
    onChange(updated)
  }
  if (tags)
    return (
      <div className="flex flex-col gap-2">
        <InspectorSubsectionLabel>{type.name}</InspectorSubsectionLabel>
        <MultiChipGroup
          values={picked}
          options={options}
          aria-label={`${type.name}, pick any`}
          onToggle={(v) => save(joinTags(picked.includes(v) ? picked.filter((p) => p !== v) : [...picked, v]))}
        />
      </div>
    )
  return (
    <div className="flex flex-col gap-2">
      <InspectorSubsectionLabel>{type.name}</InspectorSubsectionLabel>
      <FilterChipGroup
        value={current}
        onValueChange={save}
        options={options}
        variant="brand"
        size="sm"
        aria-label={type.name}
      />
    </div>
  )
}

/** How well the question separates strong from weak students. A reading, not a classification: it goes last. */
export function DiscriminationReading({ insight }: { insight: ReturnType<typeof difficultyInsightForFolder> }) {
  return <DifficultyDiscriminationReading insight={insight} />
}
// ─────────────────────────────────────────────────────────────────────────────
// Option row — grouped list: letter chip marks correct, ⋯ menu for comment/remove.
// ─────────────────────────────────────────────────────────────────────────────

interface OptionRowProps {
  letter: string
  optionNumber: number
  option: { id: string; text: string; isCorrect: boolean; rationale: string }
  locked: boolean
  isMulti: boolean
  canRemove: boolean
  /** Stem-based suggestion — placeholder only; Tab accepts into the field. */
  suggestion?: string
  enableAiRewrite?: boolean
  onLeoRewrite?: (instruction: string) => void
  onTextChange: (t: string) => void
  /**
   * Enter in this row's text field. Set only on the last row, where it
   * adds the next choice; elsewhere Enter does nothing, as before.
   */
  onEnter?: () => void
  onSelectLetter: () => void
  onToggleCorrect: () => void
  onRationaleChange: (t: string) => void
  onRemove: () => void
  onAskSelection?: (text: string) => void
}

export function OptionRow({
  letter,
  optionNumber,
  option,
  locked,
  isMulti,
  canRemove,
  suggestion = "",
  enableAiRewrite = false,
  onLeoRewrite,
  onTextChange,
  onEnter,
  onSelectLetter,
  onToggleCorrect,
  onRationaleChange,
  onRemove,
  onAskSelection,
}: OptionRowProps) {
  const [rationaleOpen, setRationaleOpen] = React.useState(
    option.rationale.length > 0
  )
  const [leoAskOpen, setLeoAskOpen] = React.useState(false)
  const optionAsk = leoComposerOptionAsk({ letter })
  const showSuggestion =
    !locked && suggestion.trim().length > 0 && option.text.trim().length === 0

  const optionInputRef = React.useRef<HTMLInputElement>(null)

  function acceptSuggestion() {
    if (!showSuggestion) return
    onTextChange(suggestion)
  }

  return (
    <div className="space-y-2">
      <BuilderListRow highlighted={option.isCorrect}>
        <Tip
          side="top"
          label={
            option.isCorrect
              ? isMulti
                ? `Option ${letter} is correct — click to unmark`
                : `Option ${letter} is the correct answer`
              : `Mark option ${letter} as correct`
          }
        >
          <BuilderAnswerChip
            letter={letter}
            correct={option.isCorrect}
            onClick={onSelectLetter}
            ariaLabel={
              option.isCorrect
                ? isMulti
                  ? `Option ${letter}, marked correct`
                  : `Option ${letter}, correct answer`
                : `Mark option ${letter} as correct`
            }
          />
        </Tip>

        {locked ? (
          <div
            className={BUILDER_LIST_LOCKED}
            aria-label={`Option ${letter} text, fixed label`}
          >
            {option.text}
          </div>
        ) : (
          <div className="relative min-w-0 flex-1">
            {showSuggestion ? (
              <div
                aria-hidden="true"
                className={cn(
                  "pointer-events-none absolute inset-0 z-0 flex min-w-0 items-center gap-2",
                  "text-base font-medium text-muted-foreground md:text-lg"
                )}
              >
                <span className="min-w-0 flex-1 truncate">{suggestion}</span>
                <span className="inline-flex shrink-0 items-center gap-1 text-xs text-muted-foreground">
                  <Kbd variant="bare">Tab</Kbd>
                </span>
              </div>
            ) : null}
            <Input
              ref={optionInputRef}
              value={option.text}
              onChange={(e) => onTextChange(e.target.value)}
              onKeyDown={(e) => {
                if (showSuggestion && e.key === "Tab" && !e.shiftKey) {
                  e.preventDefault()
                  acceptSuggestion()
                  return
                }
                // Enter adds the next choice, but only from the last row
                // and only once this one says something — otherwise a
                // stray Enter leaves a trail of empty options.
                if (e.key !== "Enter" || e.shiftKey || !onEnter) return
                if (!option.text.trim()) return
                e.preventDefault()
                onEnter()
              }}
              placeholder={showSuggestion ? " " : `Option ${optionNumber}`}
              aria-label={`Option ${letter} text`}
              className={cn(
                BUILDER_LIST_FIELD,
                "relative z-[1] bg-transparent",
                showSuggestion && "placeholder:text-transparent"
              )}
            />
            {onAskSelection ? (
              <ComposerFieldSelectionAsk
                inputRef={optionInputRef}
                onAsk={onAskSelection}
              />
            ) : null}
          </div>
        )}

        {!locked ? (
          <BuilderListRowActions>
            <div className="relative">
              <Popover open={leoAskOpen} onOpenChange={setLeoAskOpen} modal={false}>
                <PopoverAnchor asChild>
                  <span className="pointer-events-none absolute inset-0" aria-hidden />
                </PopoverAnchor>
                <PopoverContent
                  align="end"
                  side="bottom"
                  className="w-[min(26rem,calc(100vw-2rem))] overflow-hidden p-0 shadow-md"
                >
                  <LeoAskPrompt
                    question={optionAsk.question}
                    choices={optionAsk.choices}
                    appendBankOnly={false}
                    onSubmit={(instruction) => {
                      setLeoAskOpen(false)
                      onLeoRewrite?.(instruction)
                    }}
                    onClose={() => setLeoAskOpen(false)}
                  />
                </PopoverContent>
              </Popover>
            <DropdownMenu>
              <Tip side="top" label="More actions">
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    className="icon-button-chrome"
                    aria-label={`More actions for option ${letter}`}
                  >
                    <i className="fa-light fa-ellipsis" aria-hidden="true" />
                  </Button>
                </DropdownMenuTrigger>
              </Tip>
              <DropdownMenuContent align="end" className="min-w-44">
                {enableAiRewrite && onLeoRewrite ? (
                  <DropdownMenuItem
                    disabled={!option.text.trim()}
                    onSelect={() => setLeoAskOpen(true)}
                  >
                    <i
                      className="fa-duotone fa-solid fa-star-christmas text-brand"
                      aria-hidden="true"
                    />
                    Rewrite with Leo
                  </DropdownMenuItem>
                ) : null}
                <DropdownMenuItem onSelect={() => onToggleCorrect()}>
                  <i
                    className={cn(
                      "fa-light",
                      option.isCorrect ? "fa-circle-xmark" : "fa-circle-check"
                    )}
                    aria-hidden="true"
                  />
                  {option.isCorrect ? "Unmark correct" : "Mark correct"}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onSelect={() => setRationaleOpen((open) => !open)}
                >
                  <i
                    className={cn(
                      "fa-light",
                      rationaleOpen ? "fa-message-slash" : "fa-message-lines"
                    )}
                    aria-hidden="true"
                  />
                  {rationaleOpen ? "Hide comment" : "Add comment"}
                </DropdownMenuItem>
                {canRemove ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      variant="destructive"
                      onSelect={() => onRemove()}
                    >
                      <i className="fa-light fa-trash-can" aria-hidden="true" />
                      Remove option
                    </DropdownMenuItem>
                  </>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
            </div>
          </BuilderListRowActions>
        ) : null}
      </BuilderListRow>

      {rationaleOpen && !locked ? (
        <div
          className={cn(
            BUILDER_LIST_ROW,
            "flex-col items-stretch gap-2 py-2.5 ps-11"
          )}
        >
          <Textarea
            value={option.rationale}
            onChange={(e) => onRationaleChange(e.target.value)}
            placeholder={
              option.isCorrect
                ? "Why this is the single best answer (mechanism, guideline)…"
                : "Why this distractor is plausible but wrong (common confusion)…"
            }
            rows={2}
            className="min-h-[64px] resize-y border-0 bg-transparent text-sm shadow-none focus-visible:ring-0"
            aria-label={`Option ${letter} rationale`}
          />
        </div>
      ) : null}
    </div>
  )
}

function referenceKindMeta(kind: AuthoringReferenceKind) {
  return (
    AUTHORING_REFERENCE_KIND_OPTIONS.find((option) => option.value === kind) ??
    AUTHORING_REFERENCE_KIND_OPTIONS[0]
  )
}

interface ReferenceRowProps {
  index: number
  reference: AuthoringReference
  onPatch: (patch: Partial<AuthoringReference>) => void
  onRemove: () => void
  onChangeKind: (kind: AuthoringReferenceKind) => void
  onPickFile: (kind: "image" | "document") => void
}

export function ReferenceRow({
  index,
  reference,
  onPatch,
  onRemove,
  onChangeKind,
  onPickFile,
}: ReferenceRowProps) {
  const kindMeta = referenceKindMeta(reference.kind)
  const hasStudentPreview =
    (reference.kind === "image" && reference.previewUrl.length > 0) ||
    (reference.kind === "document" && reference.fileName.trim().length > 0) ||
    (reference.kind === "link" && reference.url.trim().length > 0)

  return (
    <div className="space-y-2">
      <BuilderListRow>
        <span className={builderListIndexClass(7)} aria-hidden="true">
          {index}
        </span>

        <Tip side="top" label={kindMeta.description}>
          <span
            tabIndex={0}
            role="img"
            aria-label={`${kindMeta.label} reference`}
            className={cn(
              builderListIndexClass(7),
              "border-dashed text-muted-foreground"
            )}
          >
            <i
              className={cn("fa-light text-xs", kindMeta.icon)}
              aria-hidden="true"
            />
          </span>
        </Tip>

        {reference.kind === "citation" ? (
          <Input
            value={reference.citation}
            onChange={(e) => onPatch({ citation: e.target.value })}
            placeholder="Harrison's Internal Medicine, 21st ed., ch. 269"
            aria-label={`Reference ${index} citation`}
            className={BUILDER_LIST_FIELD}
          />
        ) : null}

        {reference.kind === "link" ? (
          <Input
            value={reference.url}
            onChange={(e) => onPatch({ url: e.target.value })}
            placeholder="https://…"
            inputMode="url"
            aria-label={`Reference ${index} link URL`}
            className={BUILDER_LIST_FIELD}
          />
        ) : null}

        {reference.kind === "image" || reference.kind === "document" ? (
          reference.fileName.trim() ? (
            <div
              className={cn(BUILDER_LIST_LOCKED, "truncate text-foreground")}
              title={reference.fileName}
            >
              {reference.fileName}
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-11 min-w-0 flex-1 justify-start truncate"
              onClick={() =>
                onPickFile(reference.kind === "image" ? "image" : "document")
              }
            >
              <i className={cn("fa-light", kindMeta.icon)} aria-hidden="true" />
              Choose {reference.kind === "image" ? "image" : "document"}
            </Button>
          )
        ) : null}

        <BuilderListRowActions>
          {reference.kind === "image" || reference.kind === "document" ? (
            reference.fileName.trim() ? (
              <Tip side="top" label={`Replace ${reference.kind}`}>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="icon-button-chrome"
                  onClick={() =>
                    onPickFile(
                      reference.kind === "image" ? "image" : "document"
                    )
                  }
                  aria-label={`Replace reference ${index} ${reference.kind}`}
                >
                  <i
                    className="fa-light fa-arrow-rotate-right"
                    aria-hidden="true"
                  />
                </Button>
              </Tip>
            ) : null
          ) : null}
          <DropdownMenu>
            <Tip side="top" label="More actions">
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="icon-button-chrome"
                  aria-label={`More actions for reference ${index}`}
                >
                  <i className="fa-light fa-ellipsis" aria-hidden="true" />
                </Button>
              </DropdownMenuTrigger>
            </Tip>
            <DropdownMenuContent align="end" className="min-w-48">
              {AUTHORING_REFERENCE_KIND_OPTIONS.map((option) => (
                <DropdownMenuItem
                  key={option.value}
                  onSelect={() => onChangeKind(option.value)}
                >
                  <i
                    className={cn("fa-light", option.icon)}
                    aria-hidden="true"
                  />
                  {option.label}
                  {reference.kind === option.value ? (
                    <i
                      className="fa-solid fa-check ms-auto text-brand"
                      aria-hidden="true"
                    />
                  ) : null}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                variant="destructive"
                onSelect={() => onRemove()}
              >
                <i className="fa-light fa-trash-can" aria-hidden="true" />
                Remove reference
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </BuilderListRowActions>
      </BuilderListRow>

      {hasStudentPreview ? (
        <ReferenceStudentPreview
          index={index}
          reference={reference}
          onPatch={onPatch}
          onPickFile={onPickFile}
        />
      ) : null}
    </div>
  )
}

function ReferenceStudentPreview({
  index,
  reference,
  onPatch,
  onPickFile,
}: {
  index: number
  reference: AuthoringReference
  onPatch: (patch: Partial<AuthoringReference>) => void
  onPickFile: (kind: "image" | "document") => void
}) {
  return (
    <div
      className={cn(
        BUILDER_LIST_ROW,
        "ms-0 flex-col items-stretch gap-3 border-dashed py-3 ps-3 sm:ps-4"
      )}
    >
      <p className="text-xs font-medium text-muted-foreground">
        Students see this while answering
      </p>

      {reference.kind === "image" && reference.previewUrl ? (
        <img
          src={reference.previewUrl}
          alt={
            reference.linkLabel.trim() ||
            reference.fileName ||
            `Reference figure ${index}`
          }
          className="max-h-56 w-full rounded-md border border-border bg-muted/30 object-contain"
        />
      ) : null}

      {reference.kind === "document" && reference.fileName ? (
        <div className="flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2.5">
          <span
            className="inline-flex size-10 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground"
            aria-hidden="true"
          >
            <i className="fa-light fa-file-lines text-lg" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-foreground">
              {reference.fileName}
            </p>
            <p className="text-xs text-muted-foreground">Document attachment</p>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onPickFile("document")}
          >
            Replace
          </Button>
        </div>
      ) : null}

      {reference.kind === "link" && reference.url.trim() ? (
        <a
          href={reference.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-3 rounded-md border border-border bg-background px-3 py-2.5 text-sm font-medium text-foreground underline underline-offset-4"
        >
          <span
            className="inline-flex size-10 shrink-0 items-center justify-center rounded-md bg-brand-tint text-brand-deep"
            aria-hidden="true"
          >
            <i className="fa-light fa-arrow-up-right-from-square" />
          </span>
          <span className="min-w-0 flex-1 truncate">
            {reference.linkLabel.trim() || reference.url}
          </span>
        </a>
      ) : null}

      {reference.kind === "link" ||
      reference.kind === "image" ||
      reference.kind === "document" ? (
        <Input
          value={reference.linkLabel}
          onChange={(e) => onPatch({ linkLabel: e.target.value })}
          placeholder="Optional caption for students"
          aria-label={`Reference ${index} student caption`}
          className="h-9 text-sm"
        />
      ) : null}
    </div>
  )
}
